<template>
  <AppHeader>Deliveries</AppHeader>

  <div class="flex items-center justify-between mx-20 mt-4 text-3xl text-gray-600">
    <i @click="getDate(-1)" class="mr-4 ion-md-arrow-dropleft-circle"></i>
    <b>{{ toDate(date) }}</b>
    <i @click="getDate(1)" class="ml-4 ion-md-arrow-dropright-circle"></i>
  </div>

  <Loading v-if="state == 'loading'"></Loading>

  <div v-if="state == 'idle'" class="p-4">
    <div class="responsive-table">
      <table>
        <thead>
          <tr>
            <th>Product</th>
            <th>Count</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="p in products" :key="p.id">
            <td>{{ p.name }} ({{ p.quantity }})</td>
            <td><b>{{ p.deliveries_count }}</b></td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="mt-4 responsive-table">
      <table>
        <thead>
          <tr>
            <th>Group</th>
            <th>1L</th>
            <th>500ML</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="gd in groupDeliveries" :key="gd.id" v-show="gd['1l'] != 0 || gd['500ml'] != 0">
            <td>{{ gd.name }}</td>
            <td><b>{{ gd['1l'] }}</b></td>
            <td><b>{{ gd['500ml'] }}</b></td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="mt-4 responsive-table">
      <table>
        <thead>
          <tr>
            <th>Delivery Status</th>
            <th>Counts</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(count, status) in status_count" :key="status">
            <td class="capitalize">{{ status ? status : 'Not Updated' }}</td>
            <td><b>{{ count }}</b></td>
          </tr>
        </tbody>
      </table>
    </div>

    <SearchSort
      ref="search"
      :defaults="{q: '', search: '-', sort: 'customer.dealer_order', order: 'asc', group: '-', tag: '-', status: '-'}"
      :sorts="{id: 'Delivery ID', product_id: 'Product ID', customer_id: 'Customer ID', quantity: 'Quantity', delivery_status: 'Delivery Status', status: 'Status', returned_bottle: 'Bottle Return', 'customer.group.name': 'Group', 'customer.id': 'Customer ID', 'customer.name': 'Customer Name', 'customer.wallet_balance': 'Customer Wallet', 'customer.dealer_order': 'Customer Custom Sort'}"
      :columns="{id: 'Delivery ID', delivery_status: 'Delivery Status', 'customer.group.name': 'Group Name', 'customer.name': 'Customer Name', 'customer.address': 'Customer Address'}"
      :groups="groups"
      :tags="tags"
      :statuses="delivery_statuses"
      @update="searchUpdated"
      class="mt-8">
    </SearchSort>

    <div class="mt-4 responsive-table">
      <table>
        <thead>
          <tr>
            <th>No.</th>
            <th>Group</th>
            <th>Customer</th>
            <th>Tags</th>
            <th>Wallet</th>
            <th>Product</th>
            <th>Qty</th>
            <th @click="toggleOnlyDelivered()"><i class="ion-md-done-all"></i></th>
            <th @click="toggleDelivered()"><i class="mx-2 ion-md-return-left"></i></th>
            <th @click="toggleBottle()"><i class="mx-2 ion-md-return-right"></i></th>
            <th>Image</th>
          </tr>
        </thead>
        <tbody>
          <tr :class="{'bg-green-300': checkIfExtra(d.notes), 'bg-red-200': d.customer.wallet_balance < 100}" v-for="(d, index) in filteredData" :key="d.id">
            <td>{{ index + 1 }}</td>
            <td>{{ d.customer.group.name }}</td>
            <td><router-link :to="{name: 'ViewCustomer', params: {id: d.customer.id}}">{{ d.customer.name }} <small>(#{{ d.customer.id }})</small></router-link></td>
            <td><small>{{ d.customer.tags ? d.customer.tags.join(', ') : '' }}</small></td>
            <td>{{ toInr(d.customer.wallet_balance) }}</td>
            <td>{{ d.product.quantity }}</td>
            <td><b>{{ d.quantity }}</b></td>
            <td>
              <i v-if="d.delivery_status == 'bottle-broken'" class="ion-md-color-fill"></i>
              <i v-else-if="d.delivery_status == 'house-locked'" class="ion-md-lock"></i>
              <i v-else-if="d.delivery_status == 'customer-rejected'" class="ion-ios-hand"></i>
              <i v-else-if="d.delivery_status == 'partial-delivery'" class="ion-md-checkmark"></i>
              <i v-else-if="d.delivery_status == 'could-not-reach'" class="ion-md-close"></i>
              <i v-else-if="d.delivery_status == 'delivered'" class="ion-md-done-all"></i>
              <i v-else class="ion-md-remove"></i>
            </td>
            <td><input type="checkbox" :checked="d.status == 'delivered'" @change="updateStatus(d)"></td>
            <td><input type="checkbox" v-model="d.returned_bottle"></td>
            <td>
              <span v-if="d && d.delivery_image">
                <a :href="d.delivery_image.src" target="_system" title="View Delivery Image"><i class="ion-md-image text-green-500 px-5"></i></a>
              </span>
            </td>
          </tr>
        </tbody>
      </table>

      <div class="m-2" v-if="filteredData.length != 0">
        <button class="px-2 py-1 mr-1 text-white bg-blue-500 rounded-full" @click="updateDeliveries">Update Delivery</button>
        <!-- <button class="px-2 py-1 mr-1 text-white rounded-full bg-slate-500" @click="copyDeliveryLink">Link</button> -->
        <button class="px-2 py-1 mr-1 text-white rounded-full bg-slate-500" @click="openDeliveriesDownloadUrl">Excel</button>
        <button class="px-3 py-1 mr-1 text-white rounded-full bg-slate-500" @click="shareDeliveries"><i class="icon ion-md-share"></i></button>
      </div>
    </div>

    <div class="mt-8 responsive-table">
      <table>
        <thead>
          <tr>
            <th>No.</th>
            <th>Customer</th>
            <th>Date</th>
            <th>Days</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(vac, index) in vacations" :key="vac.id">
            <td>{{ index + 1 }}</td>
            <td><router-link :to="{name: 'ViewCustomer', params: {id: vac.customer.id}}">{{ vac.customer.name }} <small>(#{{ vac.customer.id }})</small></router-link></td>
            <td>{{ toDate(vac.start_date) }} <i class="ion-md-arrow-forward"></i> {{ toDate(vac.end_date) }}</td>
            <td>{{ diffBetweenDates(vac.start_date, vac.end_date) }}</td>
          </tr>
        </tbody>
      </table>

      <div class="m-2" v-if="vacations.length != 0">
        <button class="px-2 py-1 mr-1 text-white rounded-full bg-slate-500" @click="openVacationsDownloadUrl()">Excel</button>
      </div>
    </div>
  </div>
</template>

<script>
import { Share } from '@capacitor/share';
import mixin from '@/utils/mixin.js';
import AppHeader from '@/components/common/Header.vue';
import Loading from '@/components/common/Loading.vue';
import SearchSort from '@/components/common/SearchSort.vue';

export default {
  name: 'Deliveries',
  mixins: [ mixin ],
  components: { AppHeader, Loading, SearchSort },
  data() {
    return {
      state: 'loading',
      deliveries: [],
      products: [],
      groupDeliveries: [],
      vacations: [],
      groups: [],
      tags: [],
      delivery_statuses: [],
      status_count: [],
      date: null,
      currStatus: 'delivered',
      currBottle: true,
      filteredData: [],
    }
  },

  methods: {
    openDeliveriesDownloadUrl() {
      this.openDownloadUrl('utils/deliveries/' + btoa(this.$store.state.user.id + ':' + this.date) + '/download');
    },

    openTextDeliveriesDownloadUrl() {
      this.openDownloadUrl('utils/deliveries/' + btoa(this.$store.state.user.id + ':' + this.date) + '/download-text');
    },

    openVacationsDownloadUrl() {
      this.openDownloadUrl('utils/deliveries/vacations/' + btoa(this.$store.state.user.id + ':' + this.date));
    },

    copyDeliveryLink() {
      let deliveryLink = 'https://admin.uzhavarbumi.com/api/dealers/delivery-chart/' + btoa(this.$store.state.user.id + ':' + this.date);

      if ('clipboard' in navigator) {
        navigator
          .clipboard
          .writeText(deliveryLink)
          .then(() => {
            alert('Delivery Chart Link copied!');
          }, () => {
            window.open(deliveryLink, '_system');
          });
      } else {
        window.open(deliveryLink, '_system');
      }
    },

    shareDeliveryLink() {
      let deliveryLink = 'https://admin.uzhavarbumi.com/api/dealers/delivery-chart/' + btoa(this.$store.state.user.id + ':' + this.date);

      if (this.$store.state.platform == 'web') {
        window.open(deliveryLink, '_system');
      } else {
        Share.share({
          dialogTitle: 'Share Link with Delivery Agents',
          title: 'Delivery chart for ' + this.date,
          text: deliveryLink,
        }).then(() => {
          alert('Shared Successfully');
        });
      }
    },

    shareDeliveries() {
      if (this.$store.state.platform != 'web') {
        let shareText = 'Deliveries for ' + this.date + '\n';

        this.filteredData.forEach((item, index) => {
          shareText += `${index+1})\n*${item.customer.name} (#${item.customer.id})*\n_${item.customer.address}_\n*${item.product.name} (${item.product.quantity}) - ${item.quantity}*\n\n`;
        });

        Share.share({
          dialogTitle: 'Share with Delivery Agents',
          title: 'Deliveries for ' + this.date,
          text: shareText,
        }).then(() => {
          alert('Shared Successfully');
        });
      }
    },

    getDate(adder) {
      let t = new Date(this.date);
      t.setDate(t.getDate() + adder);

      this.date = t.getFullYear() + '-' + (t.getMonth() + 1) + '-' + t.getDate();
      this.getDeliveries();
    },

    checkIfExtra(notes) {
      if(notes == null) return false
      return notes.indexOf('EXTRA REQUEST') == -1 ? false : true;
    },

    updateStatus(d) {
      if (d.status == 'delivered') d.status = 'not_delivered';
      else d.status = 'delivered';
    },

    toggleOnlyDelivered() {
      if (!confirm('Are you sure?')) return;

      this.filteredData.forEach((item) => {
        if (item.delivery_status == 'delivered') {
          item.status = 'delivered';
        }
      });
    },

    toggleDelivered() {
      if (!confirm('Are you sure?')) return;

      this.filteredData.forEach((item) => {
        item.status = this.currStatus;
      });

      this.currStatus = this.currStatus == 'delivered' ? 'not_delivered' : 'delivered';
    },

    toggleBottle() {
      if (!confirm('Are you sure?')) return;

      this.filteredData.forEach((item) => {
        item.returned_bottle = this.currBottle;
      });

      this.currBottle = this.currBottle ? false : true;
    },

    getDeliveries() {
      this.state = 'loading';

      this.request('GET', 'deliveries/' + this.date, {}, (resp) => {
        this.state = 'idle';
        if (resp.success) {
          this.deliveries = resp.data.deliveries;
          this.products = resp.data.products;
          this.groupDeliveries = resp.data.groupDeliveries;
          this.groups = resp.data.groups;
          this.tags = resp.data.tags;
          this.delivery_statuses = resp.data.delivery_statuses;
          this.status_count = resp.data.deliveries_status_count;
          this.vacations = resp.data.vacations || [];
          this.filteredData = resp.data.deliveries;
          this.$nextTick(() => {
            this.$refs.search._emit();
          });
        } else alert(resp.message ? resp.message : 'Unable to fetch Deliveries, try again.')
      });
    },

    updateDeliveries() {
      if (!confirm('Are you sure?')) return;

      this.state = 'loading';

      let deliveries = this.deliveries.map((d) => ({'id': d.id, 'status': d.status, 'returned_bottle': d.returned_bottle}));

      this.request('POST', 'deliveries', {deliveries: deliveries}, (resp) => {
        this.state = 'idle';
        alert(resp.message);
      });
    },

    searchUpdated(q, search, sort, order, group, tag, status) {
      if (!q ||q == null || q.length == 0) this.filteredData = this.deliveries;

      if (search == '-') this.filteredData = this.deliveries.filter((item) => JSON.stringify(item).toLowerCase().includes(q.toLowerCase()));
      else this.filteredData = this.deliveries.filter((item) => JSON.stringify(this.getDeepValue(item, search)).toLowerCase().includes(q.toLowerCase()));

      if (group != '-') this.filteredData = this.filteredData.filter((item) => item.customer.group_id == group);

      if (status != '-') this.filteredData = this.filteredData.filter((item) => item.delivery_status == status);

      if (tag != '-') this.filteredData = this.filteredData.filter((item) => item.customer.tags != null ? item.customer.tags.includes(tag) : false);

      this.filteredData.sort((a, b) => {
        let aData = this.getDeepValue(a, sort), bData = this.getDeepValue(b, sort);
        return (aData == bData ? 0 : aData < bData ? -1 : 1) * (order == 'desc' ? -1 : 1);
      });
    },
  },

  created() {
    if (!this.$store.state.user.id) this.$router.push('/dash');
    this.date = this.$route.params.date;
    this.getDeliveries();
  }
}
</script>
